
<template>
	<div class="container-fluid">
		<div class="row">
			<div class="col-sm-12">
				<div class="card">
					<div class="card-header">
						<h4 class="card-title">Edit Vehicle</h4>
					</div>

					<div class="card-body">
						<!---- Add vehicle form starts ---->

						<form @submit.prevent="editVehicle" enctype="multipart/form-data">
							<div class="row">
								<div class="col-md-6 col-xl-6 form-group">
									<label class="control-label">Title <span class="required">*</span></label>
									<input type="text" v-model="vehicle.title" :class="{ 'is-invalid': Boolean(errors.title) }" minlength="3" maxlength="200" class="form-control">
									<div v-for="(val, index) in errors.title" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-6 col-xl-6 form-group">
									<label class="control-label">Price <span class="required">*</span></label>
									<input type="text" v-model="vehicle.price" :class="{ 'is-invalid': Boolean(errors.price) }" minlength="3" maxlength="200" @keypress="decimalsOnly" class="form-control">
									<div v-for="(val, index) in errors.price" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-6 form-group">
									<label class="control-label">User <span class="required">*</span></label>
									<div class="search-select">
										<span class="search-close" v-if="visible" @click="visible=false, keyword=vehicle.user_name + ' | ' + vehicle.country_code + ' ' + vehicle.user_phone">
											<i class="dripicons dripicons-cross"></i>
										</span>
										<input type="text" v-model="keyword" @keyup="fetchUsers()" :class="{ 'is-invalid': Boolean(errors.user_id) }" autocomplete="off" placeholder="Search here..." class="form-control">

										<select v-if="visible" v-model="vehicle.added_user" :size="users.length < 10 ? (users.length <= 1 ? 2 : users.length) : 10" @change="setUser" class="form-control">
											<option v-for="user in users" :key="user.id" :value="user.id">
												{{ user.name }} | {{ user.countrycode }} {{ user.phone_number }}
											</option>
										</select>
									</div>
									<div v-for="(val, index) in errors.user_id" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>	

								<div class="col-md-6 form-group">
									<label class="control-label">Category <span class="required">*</span></label>
									<select v-model="vehicle.cat_id" :class="{ 'is-invalid': Boolean(errors.cat_id) }" class="form-control">
										<option :value="null" disabled>-- Select Category --</option>
										<option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.cat_name }}</option>
									</select>
									<div v-for="(val, index) in errors.cat_id" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-6 form-group">
									<label class="control-label">Brand <span class="required">*</span></label>
									<select v-model="vehicle.brand_id" :class="{ 'is-invalid': Boolean(errors.brand_id) }" @change="fetchModels" class="form-control">
										<option :value="null" disabled>-- Select Brand --</option>
										<option v-for="brand in brands" :key="brand.id" :value="brand.id">{{ brand.brand_name }}</option>
									</select>
									<div v-for="(val, index) in errors.brand_id" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-6 form-group">
									<label class="control-label">Model <span class="required">*</span></label>
									<select v-model="vehicle.model_id" :class="{ 'is-invalid': Boolean(errors.model_id) }" class="form-control">
										<option :value="null" disabled>-- Select Model --</option>
										<option v-for="model in models" :key="model.id" :value="model.id">{{ model.name }}</option>
									</select>
									<div v-for="(val, index) in errors.model_id" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-12 col-xl-12 form-group">
									<label class="control-label">Description <span class="required">*</span></label>									
									<textarea rows="3" maxlength="600" v-model="vehicle.description" :class="{ 'is-invalid': Boolean(errors.description) }" class="form-control"></textarea>
									<div v-for="(val, index) in errors.description" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-12 mb-3">
									<label class="control-label">Location <span class="required">*</span></label>
									<GmapAutocomplete @place_changed="setPlace" class="form-control" @keypress.enter="$event.preventDefault()" placeholder="Search here..." :value="vehicle.location" @change="setLocation" :class="{ 'is-invalid': Boolean(errors.location) }" />
									<div v-for="(val, index) in errors.location" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
									<GmapMap v-if="vehicle.location"
									   :center="center"
									   :zoom="18"
									   map-style-id="roadmap"
									   :options="mapOptions"
									   style="width: 100%; height: 50vmin"
									   ref="mapRef"
									   @click="handleMapClick">
										   <GmapMarker
										      :position="marker.position"
										      :clickable="true"
										      :draggable="true"
										      @drag="handleMarkerDrag"
										      @click="panToMarker"
										   />
									</GmapMap>								  	
								</div>

								<div class="col-md-6 col-xl-6 form-group">
									<label class="control-label">Latitude <span class="required">*</span></label>
									<input type="text" v-model="marker.position.lat" :class="{ 'is-invalid': Boolean(errors.latitude) }" @keypress="decimalsOnly" class="form-control">
									<div v-for="(val, index) in errors.latitude" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-6 col-xl-6 form-group">
									<label class="control-label">Longitude <span class="required">*</span></label>
									<input type="text" v-model="marker.position.lng" :class="{ 'is-invalid': Boolean(errors.longitude) }" @keypress="decimalsOnly" class="form-control">
									<div v-for="(val, index) in errors.longitude" :key="index" class="invalid-feedback">
										<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="card">
									<div class="card-header">
										<h6 class="card-title">Attributes</h6>
									</div>
									<div class="card-body">
										<div class="row">
											<div class="col-md-6 mb-4" v-for="attr in attributes" :key="attr.id">
												<div v-if="attr.type === '1'" class="form-group attr-wrapper">
													<label class="form-check-label">{{ attr.name }}</label>
													<div class="form-check" v-for="val in attr.values" :key="val.id">
														<input
															type="radio"
															:name="'attr_' + attr.id"
															:value="val.id + '_' + val.value"
															v-model="vehicle_attributes['attr_' + attr.id]"
															class="form-check-input"
														/>
														<label class="form-check-label">{{ val.value }}</label>
													</div>
												</div>

												<div v-else-if="attr.type === '2'" class="form-group attr-wrapper">
													<label class="form-check-label">{{ attr.name }}</label>
													<div class="form-check" v-for="val in attr.values" :key="val.id">
														<input
															type="checkbox"
															:id="'attr_' + attr.id + '_' + val.id"
															:value="val.id + '_' + val.value"
															v-model="vehicle_attributes['attr_' + attr.id]"
															class="form-check-input"
														/>
														<label class="form-check-label">{{ val.value }}</label>
													</div>
												</div>

												<div v-else-if="attr.type === '3'" class="form-group attr-wrapper">
													<label class="form-check-label">{{ attr.name }}</label>
													<input
														type="text"
														v-model="vehicle_attributes['attr_' + attr.id]"
														class="form-control"
													/>
												</div>

												<div v-else-if="attr.type === '4'" class="form-group attr-wrapper">
													<label class="form-check-label">{{ attr.name }}</label>
													<textarea
														rows="3"
														v-model="vehicle_attributes['attr_' + attr.id]"
														class="form-control"
													></textarea>
												</div>
											</div>
										</div>
									</div>
								</div>


								 <div class="col-md-12">
									<div class="input-dynamic" :class="{ 'is-invalid': Boolean(errors.images) }">
									<label class="control-label">Images <span class="required">*</span></label>

									<div class="row">
										<div class="col-md-12 mb-3"  v-for="(img, index) in vehicle.images" :key="img.id">
										<div class="delete-image-wrapper">
											<img :src="$image_url + img.image" />
											<span class="delete-image" @click="deleteImage(img.id, index)">
											<i class="fa fa-minus-circle text-danger"></i>
											</span>
										</div>
										</div>
									</div>

									<div class="row" v-for="(input, index) in inputs" :key="index">
										<div class="col-md-6 form-group">
										<input type="file" accept=".jpg, .png, .jpeg" @change="setFile($event, index)" class="form-control"/>
										<span>
											<i
											class="fa fa-minus-circle text-danger"
											@click="remove(index)"
											v-show="index || (!index && inputs.length > 1)"
											></i>
											<i
											class="fa fa-plus-circle text-green"
											@click="add()"
											v-show="index === inputs.length - 1"
											></i>
										</span>
										</div>
										<div class="col-md-6">
										<img class="image-preview" :src="input.preview_image" />
										</div>
									</div>
									</div>
									<div v-for="(val, index) in errors.images" :key="index" class="invalid-feedback">
									<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<!-- Video Section -->
								<div class="col-md-12 mt-3">
									<label class="control-label">Video</label>
									<input
									type="file"
									accept=".mp4, .ogx, .oga, .ogv, .ogg, .webm"
									@change="setVideo($event)"
									:class="{ 'is-invalid': Boolean(errors.video) }"
									class="form-control"
									/>
									<div class="info-message">Leave this field empty if you don't want to add/edit the video</div>
									<div v-for="(val, index) in errors.video" :key="index" class="invalid-feedback">
									<p class="mb-0">{{ val }}</p>
									</div>
								</div>

								<div class="col-md-12 mt-4 text-end">
									<button type="submit" class="btn btn-primary me-2">Update</button>
									<button type="button" @click="cancelEdit" class="btn btn-primary">Cancel</button>
								</div>


							</div>
						</form>

						<!---- Add vehicle form ends ---->
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
	export default {
		name: "EditVehicle",
		

		data() {
			
			return {
				vehicle: {
					title: '',
					brand_id: null,
					model_id: null,
					cat_id: null,
					added_user: null,
					user_name: '',
					user_email: '',
					user_phone: '',
					description: '',
					price: null,
					location: '',
					latitude: null,
					longitude: null,
					video: '',					
				},
				
				marker: { position: { lat: 10, lng: 10 } },
			   center: { lat: 10, lng: 10 },
			   current_place: null,

			   mapOptions: {
			      disableDefaultUI: true,
			   },

				users: {},
				categories: {},
				brands: {},
				models: {},
				attributes: [],
				inputs: [
	            {value: null, preview_image: null, image: null}
	         ],
	         vehicle_attributes: {},
	         images: [],
	         image_id: [],
				attribute_ids: [],
				attribute_value_ids: [],
				attribute_values: [],
				keyword: '',
				visible: false,
				token: "",
				errors: {},
			};
		},
		mounted() {
			
		},

		created() {
			this.fetchVehicle();
			this.fetchCategories();
			this.fetchBrands();
			this.fetchAttributes();
			//this.geolocate();

		},

		methods: {			

			add() {
	         this.inputs.push({ value: null });
	      },

	      remove(index) {
	         this.inputs.splice(index, 1);
	         this.images.splice(index, 1);
	      }, 

	      setFile(e, index) {
	      	this.images.push(e.target.files[0]);
	      	this.inputs[index].image = e.target.files[0];
				this.inputs[index].preview_image = URL.createObjectURL(e.target.files[0]);
			},

			deleteImage(id, index) {
				this.$swal({
					icon: 'warning',
					title: "Confirm",
					text: 'Are you sure you want to delete this image?',
					showCancelButton: true,
					confirmButtonText: 'Yes',
					confirmButtonColor: '#e64942',
					cancelButtonText: 'No',
					cancelButtonColor: '#efefef',
					position: "center",
					showConfirmButton: true,
					toast: false,
				})
				.then(result => {
					if (result.isConfirmed) {
						this.vehicle.images.splice(index, 1);
						this.image_id.push(id);
						this.$swal('Deleted!', 'The image has been deleted.', 'success');
					}
				})
				.catch(err => {
					console.log(err);
				});
			},


			geolocate() {
		      navigator.geolocation.getCurrentPosition((position) => {
		         this.marker.position = {
		           lat: position.coords.latitude,
		           lng: position.coords.longitude,
		         };
		         this.panToMarker();
      		});
    		},

		   //sets the position of marker when dragged
		   handleMarkerDrag(e) {
		      this.marker.position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
		   },

		   //Moves the map view port to marker
		   panToMarker() {
		      this.$refs.mapRef.panTo(this.marker.position);
		      this.$refs.mapRef.setZoom(18);
		   },

		   //Moves the marker to click position on the map
		   handleMapClick(e) {
		      this.marker.position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
		      console.log(e);
		   },		 

		 	setPlace(place) {
		    	this.current_place = place;
		    	if(this.current_place) {
		    		const marker = {
			    		lat: this.current_place.geometry.location.lat(),
			    		lng: this.current_place.geometry.location.lng(),
			    	};
			    	this.center = marker;
			    	this.marker.position = marker;
			    	this.vehicle.location = place.formatted_address;
		    	}
		   },

		   setLocation(e) {
		   	this.vehicle.location = e.target.value;
		   },

			decimalsOnly(event) {
		      let keyCode = event.keyCode;
		      if (keyCode < 48 || keyCode > 57) {
		        event.preventDefault();
		      }
		    },
		    alphaSpacesOnly(event) {
		    	let keyCode = event.keyCode;
			    if ((keyCode >= 65 && keyCode <= 90) || 
			    	(keyCode >= 97 && keyCode <= 122) ||			    	
			    	(keyCode == 32)) {
			        return;
			    } else {
			    	event.preventDefault();
			    }
		    },
		   
			getToken() {
         if (localStorage.getItem('slo_admin')) {
            var slo_admin =  JSON.parse(localStorage.getItem('slo_admin')); 
                    
            if(slo_admin.token) {
               this.token = slo_admin.token;
               this.user = slo_admin;

            } else {
               this.$swal("", "Failed to fetch token, please login again", "error");
               this.$router.push({ name: "Login" });
            }

         } else {
            this.$swal("", "Session expired, please login again", "error");
            this.$router.push({ name: "Login" });
         }
      },

		fetchVehicle() {
			let url = '/api/admin/vehicles/show?vehicle_id=' + this.$route.params.id;

			this.getToken();
			this.$axios
				.get(url, { headers: { Authorization: 'Bearer ' + this.token } })
				.then(res => {
					var responseData = res.data.data;
					this.vehicle = responseData;
					console.log(this.vehicle);
					this.fetchModels();
					this.marker.position.lat = parseFloat(this.vehicle.latitude);
					this.marker.position.lng = parseFloat(this.vehicle.longitude);

					this.center.lat = parseFloat(this.vehicle.latitude);
					this.center.lng = parseFloat(this.vehicle.longitude);

					this.keyword = this.vehicle.user_name + ' | ' + this.vehicle.country_code + ' ' + this.vehicle.user_phone;

					this.vehicle.attributes.forEach(obj => {
                        let attr_k = 'attr_' + obj.attribute_id;
                        let attr_v = obj.value_id + '_' + obj.value;

                        // Handle different attribute types
                        if (obj.type == '2') {
                            // Checkboxes
                            if (!this.vehicle_attributes[attr_k]) {
                                this.vehicle_attributes[attr_k] = [];
                            }
                            if (!this.vehicle_attributes[attr_k].includes(attr_v)) {
                                this.vehicle_attributes[attr_k].push(attr_v);
                            }
                        } else {
                            // Radio buttons and others
                            this.vehicle_attributes[attr_k] = attr_v;
                        }
                    });


					console.log(this.vehicle_attributes);
				})
				.catch(err => console.log(err));
		},


			fetchCategories() {
				let url = "/api/admin/vehicles/category";			
				this.getToken();
				this.$axios
					.get(url, { headers: { Authorization: "Bearer " + this.token } })
					.then((res) => {
						var responseData = res.data;
						this.categories = responseData.data;
					})
					.catch((err) => console.log(err));
			},

			fetchBrands() {
				let url = "/api/admin/vehicles/brands";				

				this.getToken();
				this.$axios
					.get(url, { headers: { Authorization: "Bearer " + this.token } })
					.then((res) => {
						var responseData = res.data;
						this.brands = responseData.data;
					})
					.catch((err) => console.log(err));
			},

			fetchModels() {
				
				let url = "/api/admin/vehicles/models?brand_id=" + this.vehicle.brand_id;		
				this.getToken();
				this.$axios
					.get(url, { headers: { Authorization: "Bearer " + this.token } })
					.then((res) => {
						var responseData = res.data;
						this.models = responseData.data;
					})
					.catch((err) => console.log(err));
			},

			fetchUsers() {
				let url = "/api/admin/users?limit=0&keyword=" + this.keyword;
				this.getToken();
				this.$axios
					.get(url, {
						headers: { Authorization: "Bearer " + this.token },
					})
					.then((res) => {
						var responseData = res.data.data;
						this.users = responseData.data;
						//console.log(this.users);
						this.visible = true;
						this.vehicle.user_id = null;
					})
					.catch((err) => console.log(err));
			},

			fetchAttributes() {
				let url = "/api/admin/vehicles/attributes";
				this.$axios
					.get(url, {
						headers: { Authorization: "Bearer " + this.token },
					})
					.then((res) => {
						var responseData = res.data.data;
						this.attributes = responseData;
						console.log(responseData);
						
					})
					.catch((err) => console.log(err));
			},

			setUser() {
				//alert(this.vehicle.user_id.text);
				this.keyword = event.target.options[event.target.options.selectedIndex].label;
				//this.keyword = this.vehicle.user_id;
				this.visible = false;
			},

			setVideo(event) {
				this.vehicle.video = event.target.files[0];
			},

			editVehicle() {

				this.getToken();
				let formData = new FormData();
				formData.append("vehicle_id", this.vehicle.id);
				formData.append("title", this.vehicle.title);
				formData.append("price", this.vehicle.price);
				formData.append("cat_id", this.vehicle.cat_id);
				formData.append("user_id", this.vehicle.added_user);
				formData.append("brand_id", this.vehicle.brand_id);
				formData.append("model_id", this.vehicle.model_id);
				formData.append("description", this.vehicle.description);
				formData.append("location", this.vehicle.location);
				formData.append("latitude", this.marker.position.lat);
				formData.append("longitude", this.marker.position.lng);
				formData.append("video", this.vehicle.video);
			

				/************************************************
				In the key->value pair of vehicle_attributes,
				key is attrtype_attrid,
				value is just value for textbox, textarea and
				value is valueid_value for radio, checkbox, grid. 
				**************************************************/

	         for(var key of Object.keys(this.vehicle_attributes)) {

	         	let key_array = key.split("_");	      	
	         	
	         	// if attr type is texbox/ textarea
	         	if(key_array[0] == 3 || key_array[0] == 4 || key_array[0] == 5) { 
	         		this.attribute_ids.push(key_array[1]);
	         		this.attribute_value_ids.push('0');
	         		this.attribute_values.push(this.vehicle_attributes[key]);


	         	} else {
	         		// if attr type is checkbox, value will be array
	         		if(key_array[0] == 2) {
	         			let chk_array = this.vehicle_attributes[key];
	         			for(var i = 0; i < chk_array.length; i++) {
	         				this.attribute_ids.push(key_array[1]);
	         				let value_array = chk_array[i].split('_');
	         				this.attribute_value_ids.push(value_array[0]);
	         				this.attribute_values.push(value_array[1]);
	         			}
	         		} else {
	         			let array = this.vehicle_attributes[key].split("_");
	         			this.attribute_value_ids.push(array[0]);
	         			this.attribute_values.push(array[1]);
	         			this.attribute_ids.push(key_array[1]);
	         		}
	         	}	         	
	         }

	         //console.log('attribute_ids', this.attribute_ids);
	         //console.log('attribute_value_ids', this.attribute_value_ids);
	         //console.log('attribute_values', this.attribute_values);


	         formData.append('attribute_value_ids', JSON.stringify(this.attribute_value_ids));
	         formData.append('attribute_values', JSON.stringify(this.attribute_values));
	         formData.append('attribute_ids', JSON.stringify(this.attribute_ids));
	         formData.append('image_id', JSON.stringify(this.image_id));

	         //console.log(this.images);
				   

				for(var j = 0; j < this.images.length; j++) {
				  	let file = this.images[j];
				   formData.append('images[' + j + ']', file);
				}

				const config = {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				};

				let url;
				url = "/api/admin/vehicles/edit";

				this.$axios
					.post(url, formData, config)
					.then((response) => {
						if (response.data.status) {
							this.$swal("", response.data.message, "success");
						} else {
							this.$swal("", response.data.message, "error");
						}
						this.resetForm();
					})
					.catch((error) => {
						this.errors = {};
						if (error.response) {
							if (error.response.data.message) {
								console.log(error.response.data);
								this.errors = error.response.data.message;
								console.log(this.errors);
								//this.$vToastify.error(error.response.message);
							} else {
								this.$swal("", "Something went wrong", "error");
							}
						} else {
							this.$swal("", "Something went wrong", "error");
						}
					});
			},

			resetForm() {				
				this.fetchVehicle();
				this.fetchCategories();
				this.fetchBrands();
				this.fetchAttributes();
				this.inputs = [{value: null, preview_image: null}];
				this.images = [];
				this.image_id = [];	
				this.attribute_ids = [];
				this.attribute_value_ids = [];
				this.attribute_values = [];
				this.errors = {};
				this.vehicle_attributes={};
				this.vehicle = {
                title: '',
                brand_id: null,
                model_id: null,
                cat_id: null,
                added_user: null,
                user_name: '',
                user_email: '',
                user_phone: '',
                description: '',
                price: null,
                location: '',
                latitude: null,
                longitude: null,
                video: '',
            };

				
			},
		},
	};
</script>
<style>
.note {
    margin-left: 10px; 
    white-space: nowrap; 
    font-size: 0.875rem; 
}
</style>